// PAGADO es una marca de cobro, no un paso del flujo de la comanda.
//
// El backend guarda el pago como un registro más en `state_orders`, pero una
// línea puede estar pagada y seguir PENDIENTE / EN CURSO / ENTREGADO. Aquí se
// separan las dos cosas: el estado de cocina (último estado sin PAGADO) y si
// la línea está pagada.

export const PAID_STATE = 'PAGADO';
export const CANCELLED_STATES = ['CANCELADO', 'CANCELADO POR CLIENTE'];

function eventStateName(event) {
  const state = event?.state;
  return String((state && typeof state === 'object' ? state.name : state) || '').trim().toUpperCase();
}

function eventTime(event) {
  const time = Date.parse(event?.created_at || '');
  return Number.isFinite(time) ? time : 0;
}

export function sortedStateEvents(line) {
  if (!Array.isArray(line?.state_orders)) return [];
  return [...line.state_orders]
    .filter((event) => eventStateName(event))
    .sort((a, b) => eventTime(b) - eventTime(a));
}

/** Último registro de estado de cocina (sin la marca de pago), o null. */
export function lineServiceEvent(line) {
  return sortedStateEvents(line).find((event) => eventStateName(event) !== PAID_STATE) || null;
}

/** Estado de cocina de la línea. */
export function lineServiceState(line, fallback = 'PENDIENTE') {
  const event = lineServiceEvent(line);
  if (event) return eventStateName(event);
  const explicit = String(line?.service_state || line?.kitchen_state || line?.state || '').trim().toUpperCase();
  return explicit && explicit !== PAID_STATE ? explicit : fallback;
}

/** La línea tiene marca de pago (aunque luego haya avanzado en cocina). */
export function isLinePaid(line) {
  if (typeof line?.is_paid === 'boolean') return line.is_paid;
  if (typeof line?.paid === 'boolean') return line.paid;
  if (sortedStateEvents(line).some((event) => eventStateName(event) === PAID_STATE)) return true;
  return String(line?.state || '').trim().toUpperCase() === PAID_STATE;
}

export function isLineCancelled(line) {
  return CANCELLED_STATES.includes(lineServiceState(line, ''));
}

function lineAmount(line) {
  if (line?.is_2x1_discount) return 0;
  const units = Number(line?.amount ?? line?.quantity ?? 0);
  const unitPrice = Number(line?.unit_price ?? 0);
  return Number.isFinite(units * unitPrice) ? units * unitPrice : 0;
}

/**
 * Estado de cobro del pedido: 'paid' | 'partial' | 'unpaid' | null.
 * Solo cuentan las líneas con importe y no canceladas.
 */
export function orderPaymentStatus(lines = []) {
  const live = (Array.isArray(lines) ? lines : []).filter((line) => !isLineCancelled(line));
  const chargeable = live.filter((line) => lineAmount(line) > 0);
  if (!chargeable.length) {
    return live.some((line) => isLinePaid(line)) ? 'paid' : null;
  }
  const paid = chargeable.filter((line) => isLinePaid(line)).length;
  if (paid === chargeable.length) return 'paid';
  return paid > 0 ? 'partial' : 'unpaid';
}

/** Importe que falta por cobrar: líneas con importe, ni pagadas ni canceladas. */
export function orderPendingAmount(lines = []) {
  const total = (Array.isArray(lines) ? lines : [])
    .filter((line) => !isLineCancelled(line) && !isLinePaid(line))
    .reduce((sum, line) => sum + lineAmount(line), 0);
  return Math.round(total * 100) / 100;
}

export const PAYMENT_STATUS_LABELS = {
  paid: 'Pagado',
  partial: 'Pago parcial',
  unpaid: 'Sin cobrar',
};
